import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { checkPassword } from '../lib/passwordPolicy';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';
import { PasswordStrengthMeter } from '../components/ui/PasswordStrengthMeter';
import { CMYKMark } from '../components/landing/CMYKMark';

type Phase = 'verifying' | 'ready' | 'submitting' | 'done' | 'invalid';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');

  // Quando o usuário chega aqui via link do e-mail, o Supabase passa
  // os tokens no fragmento (#access_token=...&type=recovery). O cliente
  // com detectSessionInUrl: true consome e dispara onAuthStateChange.
  useEffect(() => {
    let cancelled = false;

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' && session?.user) {
        setEmail(session.user.email ?? '');
        setPhase('ready');
      }
    });

    // Fallback: se o evento já passou antes do listener montar,
    // checa se há sessão de recovery via getSession.
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session?.user) {
        setEmail(data.session.user.email ?? '');
        setPhase((p) => (p === 'verifying' ? 'ready' : p));
      } else {
        // Sem token no URL e sem sessão de recovery: o link está inválido
        // ou expirou. Damos uns segundos pro onAuthStateChange disparar antes.
        setTimeout(() => {
          if (!cancelled) {
            setPhase((p) => (p === 'verifying' ? 'invalid' : p));
          }
        }, 1500);
      }
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const passwordCheck = checkPassword(password, email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!passwordCheck.ok) {
      setError('A senha deve atingir o nível Forte (verde) para ser aceita.');
      return;
    }
    if (password !== confirm) {
      setError('As senhas digitadas não coincidem. Confira e tente novamente.');
      return;
    }

    setPhase('submitting');
    const { error: err } = await supabase.auth.updateUser({ password });

    if (err) {
      const raw = (err.message || '').toLowerCase();
      if (raw.includes('same') || raw.includes('different')) {
        setError('A nova senha precisa ser diferente da senha atual.');
      } else if (raw.includes('weak') || raw.includes('short')) {
        setError('A senha não atende aos critérios de segurança. Use uma senha mais forte.');
      } else if (raw.includes('session') || raw.includes('expired') || raw.includes('jwt')) {
        setError('Sua sessão de recuperação expirou. Solicite um novo link de redefinição.');
      } else {
        setError('Não foi possível redefinir a senha agora. Tente novamente em instantes.');
      }
      setPhase('ready');
      return;
    }

    setPhase('done');
    setTimeout(() => navigate('/album', { replace: true }), 1500);
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper bt-paper-grain">
      <div className="px-6 md:px-10 pt-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-small font-semibold text-ink-500 hover:text-ink-900 transition-colors btn-focus rounded-md py-1"
        >
          <ArrowLeft size={16} />
          Voltar para login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 md:px-10 py-8">
        <div className="w-full max-w-md">
          <div className="mb-7">
            <Logo variant="on-light" size="lg" markSize={32} />
          </div>

          {phase === 'verifying' && (
            <div className="flex items-center gap-2 text-ink-700">
              <Loader2 size={18} className="animate-spin text-green-700" />
              <span className="font-body text-small">Verificando link de redefinição...</span>
            </div>
          )}

          {phase === 'invalid' && (
            <>
              <p className="bt-eyebrow text-ruby-700 mb-2">Link inválido</p>
              <h1 className="font-display font-bold text-h1 text-ink-900 leading-tight tracking-[-0.02em]">
                Este link não funciona mais.
              </h1>
              <p className="font-body text-body text-ink-700 mt-2">
                Pode ter expirado, já sido usado ou estar incompleto. Solicite um novo link de
                redefinição.
              </p>
              <div className="mt-6">
                <Link to="/esqueci-senha">
                  <Button variant="primary" size="lg" iconRight={ArrowRight}>
                    Pedir novo link
                  </Button>
                </Link>
              </div>
            </>
          )}

          {phase === 'done' && (
            <>
              <div className="bg-green-100 border border-green-700 text-green-900 text-small rounded-lg p-4 inline-flex items-start gap-2">
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                <span>Senha redefinida com sucesso. Redirecionando para o álbum...</span>
              </div>
            </>
          )}

          {(phase === 'ready' || phase === 'submitting') && (
            <>
              <p className="bt-eyebrow text-green-700 mb-2">Nova senha</p>
              <h1 className="font-display font-bold text-h1 text-ink-900 leading-tight tracking-[-0.02em]">
                Crie sua nova senha.
              </h1>
              <p className="font-body text-body text-ink-700 mt-2">
                {email ? `Para a conta ${email}.` : 'Escolha uma senha forte.'}
              </p>

              {error && (
                <div className="bg-ruby-100 border border-ruby-500 text-ruby-700 text-small rounded-lg p-3 mt-5 inline-flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <div>
                  <label htmlFor="pwd" className="block bt-eyebrow text-ink-500 mb-1.5">
                    Nova senha
                  </label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                    />
                    <input
                      id="pwd"
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                      required
                      minLength={10}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full h-11 pl-9 pr-10 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? 'Esconder senha' : 'Mostrar senha'}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900 p-1 rounded-md btn-focus"
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <PasswordStrengthMeter check={passwordCheck} />
                </div>

                <div>
                  <label htmlFor="confirm" className="block bt-eyebrow text-ink-500 mb-1.5">
                    Confirmar nova senha
                  </label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                    />
                    <input
                      id="confirm"
                      type={showPwd ? 'text' : 'password'}
                      value={confirm}
                      onChange={(ev) => setConfirm(ev.target.value)}
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full h-11 pl-9 pr-10 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  iconRight={ArrowRight}
                  loading={phase === 'submitting'}
                  disabled={!passwordCheck.ok || phase === 'submitting'}
                >
                  Redefinir senha
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="px-6 md:px-10 pb-6 flex items-center justify-between text-caption text-ink-500">
        <span className="bt-eyebrow text-ink-500/70">BoraTrocar</span>
        <CMYKMark className="opacity-30" />
      </div>
    </div>
  );
}
